import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import moment from 'moment-timezone'

import PerText from '../Text'
import PerButton from '../Button'
import PerSpacer from '../Spacer'
import PerFlexRow from '../FlexRow'
import { client } from '../../..'
import { useAuth } from '../../../context/auth-context'
import { ME_QUERY } from '../../../graphql/me.query'
import { changeLocale, changeTimezone } from '../../../graphql/profiles/locales'
import { changeNameDisplayPreference } from '../../../graphql/profiles/nameDisplayPreferences'
import { useSettingsModalStore } from './SettingsModalStore'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'

export const SettingsModalFooter: FC = () => {
  const { t } = useTranslation()
  const { user, setUser } = useAuth()
  const [isProcessing, toggleProcessing] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const [toggleModal, settingsForm] = useSettingsModalStore((state) => [state.toggleModal, state.settingsForm], shallow)

  const handleClose = () => {
    toggleModal(false)
  }

  const handleSubmit = async () => {
    toggleProcessing(true)
    const formValues = !!settingsForm ? settingsForm.getValues() : null
    if (!!formValues && !!formValues.locale && !!formValues.nameDisplayPreference && !!formValues.timezone) {
      const { errors: errorA } = await changeLocale(user.employeeId, formValues.locale)
      const { errors: errorB } = await changeTimezone(user.employeeId, formValues.timezone)
      const { errors: errorC } = await changeNameDisplayPreference(
        user.employeeId,
        formValues.nameDisplayPreference as NameDisplayPreference,
      )
      if (!!errorA || !!errorB || !!errorC) {
        setErrorMessage(t('settingsModal.settingsError'))
        toggleProcessing(false)
      } else {
        const { data, errors } = await client.query({ query: ME_QUERY, fetchPolicy: 'network-only' })
        if (!!errors) {
          setErrorMessage(t('settingsModal.settingsError'))
          toggleProcessing(false)
        } else {
          moment.locale(data.me.locale)
          setUser(data.me)
          handleClose()
        }
      }
    }
  }

  return (
    <PerFlexRow align="middle" justify="space-between">
      <PerFlexRow>
        <PerButton
          text={t('global.button.cancel')}
          color="secondary"
          before="close_stroke"
          variant="pastel"
          type="button"
          onClick={handleClose}
        />
      </PerFlexRow>
      <PerFlexRow align="middle">
        {!!errorMessage && (
          <PerText appearance="info" color="error">
            {errorMessage}
          </PerText>
        )}
        <PerSpacer ml="xxxs" mr="xxxs" />
        <PerButton
          text={t('global.button.save')}
          color="success"
          before="check_stroke"
          variant="pastel"
          type="button"
          isProcessing={{ active: isProcessing }}
          onClick={handleSubmit}
          data-e2e-settings-modal-button-confirm
        />
      </PerFlexRow>
    </PerFlexRow>
  )
}
